import { useState } from 'react';
import { Surface, Badge, Button, Grid, PageHeader, Input, Modal } from '../../ui/components';
import { Plus, Eye, Landmark, Edit } from 'lucide-react';
import FloatingOwenAssistant from '../../components/common/FloatingOwenAssistant';
import FloatingKnowledge from '../../components/common/FloatingKnowledge';

const DetailRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--lk-border-subtle)', fontSize: 'var(--lk-text-sm)' }}>
    <span style={{ color: 'var(--lk-text-tertiary)', fontWeight: 500 }}>{label}</span>
    <span style={{ color: 'var(--lk-text-primary)' }}>{value}</span>
  </div>
);

const mockLenders = [
  {
    id: '1',
    company_name: 'First National Bank',
    email: 'lending@firstnational.com',
    license_states: ['TX', 'CA', 'FL', 'NY'],
    loan_types: ['Conventional', 'FHA', 'VA', 'Jumbo'],
    is_active: true,
    total_bids: 45,
    accepted_bids: 12,
    avg_rate: 6.75,
  },
  {
    id: '2',
    company_name: 'Community Credit Union',
    email: 'loans@communitycu.org',
    license_states: ['TX', 'OK', 'AR'],
    loan_types: ['Conventional', 'FHA', 'USDA'],
    is_active: true,
    total_bids: 23,
    accepted_bids: 8,
    avg_rate: 6.85,
  },
];

const AdminLenders = () => {
  const [lenders] = useState(mockLenders);
  const [selectedLender, setSelectedLender] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const handleViewDetails = (lender) => {
    setSelectedLender(lender);
    setDetailsOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Lenders"
        description="Manage lender network and partnerships"
        actions={
          <Button variant="primary" icon={Plus} onClick={() => setAddOpen(true)}>
            Add Lender
          </Button>
        }
      />

      {/* Stats */}
      <Grid cols={4} gap="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-6)' }}>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Total Lenders</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700 }}>{lenders.length}</div>
        </Surface>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Active Lenders</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700, color: 'var(--lk-success-600)' }}>
            {lenders.filter(l => l.is_active).length}
          </div>
        </Surface>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Total Bids</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700 }}>
            {lenders.reduce((sum, l) => sum + l.total_bids, 0)}
          </div>
        </Surface>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Accepted Bids</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700, color: 'var(--lk-primary-600)' }}>
            {lenders.reduce((sum, l) => sum + l.accepted_bids, 0)}
          </div>
        </Surface>
      </Grid>

      {/* Lenders Table */}
      <Surface padding="0" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="lk-table">
            <thead>
              <tr>
                <th>Lender</th>
                <th>Licensed States</th>
                <th>Loan Types</th>
                <th>Total Bids</th>
                <th>Accepted</th>
                <th>Avg Rate</th>
                <th>Status</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {lenders.map((lender) => (
                <tr key={lender.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
                      <div style={{ width: 32, height: 32, borderRadius: 'var(--lk-radius-md)', backgroundColor: 'var(--lk-primary-50)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Landmark size={16} color="var(--lk-primary-500)" />
                      </div>
                      <div>
                        <div style={{ fontWeight: 500 }}>{lender.company_name}</div>
                        <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)' }}>{lender.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {lender.license_states.slice(0, 3).map(state => (
                        <Badge key={state} variant="neutral">{state}</Badge>
                      ))}
                      {lender.license_states.length > 3 && (
                        <Badge variant="neutral">+{lender.license_states.length - 3}</Badge>
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {lender.loan_types.slice(0, 2).map(type => (
                        <Badge key={type} variant="primary">{type}</Badge>
                      ))}
                      {lender.loan_types.length > 2 && (
                        <Badge variant="primary">+{lender.loan_types.length - 2}</Badge>
                      )}
                    </div>
                  </td>
                  <td style={{ fontWeight: 600 }}>{lender.total_bids}</td>
                  <td style={{ fontWeight: 600, color: 'var(--lk-success-600)' }}>{lender.accepted_bids}</td>
                  <td style={{ fontWeight: 600 }}>{Number(lender.avg_rate).toFixed(3)}%</td>
                  <td>
                    <Badge variant={lender.is_active ? 'success' : 'neutral'}>
                      {lender.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <Button size="sm" variant="ghost" icon={Eye} onClick={() => handleViewDetails(lender)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Surface>

      {/* Details Modal */}
      <Modal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        title="Lender Details"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDetailsOpen(false)}>Close</Button>
            <Button variant="primary" icon={Edit}>Edit</Button>
          </>
        }
      >
        {selectedLender && (
          <>
            <Grid cols={2} gap="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
              <Surface padding="var(--lk-space-5)">
                <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Company Info</div>
                <DetailRow label="Name" value={selectedLender.company_name} />
                <DetailRow label="Email" value={selectedLender.email} />
                <DetailRow label="Status" value={
                  <Badge variant={selectedLender.is_active ? 'success' : 'neutral'}>
                    {selectedLender.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                } />
              </Surface>
              <Surface padding="var(--lk-space-5)">
                <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Performance</div>
                <DetailRow label="Total Bids" value={selectedLender.total_bids} />
                <DetailRow label="Accepted" value={selectedLender.accepted_bids} />
                <DetailRow label="Avg Rate" value={`${Number(selectedLender.avg_rate).toFixed(3)}%`} />
                <DetailRow label="Win Rate" value={`${((selectedLender.accepted_bids / selectedLender.total_bids) * 100).toFixed(1)}%`} />
              </Surface>
            </Grid>

            <Surface padding="var(--lk-space-5)" style={{ marginBottom: 'var(--lk-space-4)' }}>
              <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Licensed States</div>
              <div style={{ display: 'flex', gap: 'var(--lk-space-2)', flexWrap: 'wrap' }}>
                {selectedLender.license_states.map(state => (
                  <Badge key={state} variant="neutral">{state}</Badge>
                ))}
              </div>
            </Surface>

            <Surface padding="var(--lk-space-5)">
              <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Loan Types</div>
              <div style={{ display: 'flex', gap: 'var(--lk-space-2)', flexWrap: 'wrap' }}>
                {selectedLender.loan_types.map(type => (
                  <Badge key={type} variant="primary">{type}</Badge>
                ))}
              </div>
            </Surface>
          </>
        )}
      </Modal>

      {/* Add Modal */}
      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add Lender"
        footer={
          <>
            <Button variant="secondary" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button variant="primary">Add Lender</Button>
          </>
        }
      >
        <Input label="Company Name" placeholder="Enter company name" />
        <Input label="Email" type="email" placeholder="Enter email" style={{ marginTop: 'var(--lk-space-3)' }} />
        <Input label="Licensed States" placeholder="e.g., TX, CA, FL" style={{ marginTop: 'var(--lk-space-3)' }} />
        <Input label="Loan Types" placeholder="e.g., Conventional, FHA, VA" style={{ marginTop: 'var(--lk-space-3)' }} />
      </Modal>

      <FloatingOwenAssistant context="admin-lenders" />
      <FloatingKnowledge customContent={{
        label: 'Lenders',
        title: 'Lender management',
        body: 'Manage lender accounts and monitor marketplace activity.',
        bullets: ['Track bid activity', 'Monitor win rates', 'Manage account status'],
        resources: [{ label: 'Lender onboarding', href: '#' }],
      }} />
    </div>
  );
};

export default AdminLenders;
